<template>
  <div class="rm-full-page">

    <!-- ── Header — matches applications project exactly (no language dropdown) ── -->
    <div class="app-header">
      <div class="app-header-content">
        <div class="dba-logo-container">
          <img class="dba-logo" src="https://consumer-portal-public-bucket.s3.amazonaws.com/goldstar_logo_2015NMLStop.svg" alt="Gold Star Mortgage" />
        </div>
        <div class="lo-info-container" v-if="loProfile.name">
          <div class="lo-text">
            <p class="lo-name">{{ loProfile.name }}</p>
            <p class="lo-mobile link" v-if="loProfile.phone">
              <a :href="'tel:' + loProfile.phone">{{ loProfile.phone }}</a>
            </p>
          </div>
          <div class="lo-img-container" v-if="loProfile.photo">
            <img class="lo-img" :src="loProfile.photo" :alt="loProfile.name" />
          </div>
        </div>
      </div>
    </div>

    <!-- ── Main content ── -->
    <div class="home-view rm-home-view">
      <div class="home-content">

        <div class="form-container">

          <!-- ── SUCCESS SCREEN ── -->
          <transition name="success-fade">
            <div class="rm-success-screen" v-if="showSubmitted">
              <div class="rm-success-inner">
                <div class="rm-success-icon">
                  <i class="fa-duotone fa-solid fa-circle-check"></i>
                </div>
                <h2 class="rm-success-title">Request Submitted Successfully!</h2>
                <p class="rm-success-msg">
                  Your Reverse Mortgage Request has been received. The team will review the information and reach out shortly to guide you through the next steps.
                </p>
                <div class="rm-success-details">
                  <div class="rm-success-detail-row" v-if="form.bwrFullName">
                    <span class="rm-success-detail-label">Borrower</span>
                    <span class="rm-success-detail-value">{{ form.bwrFullName }}</span>
                  </div>
                  <div class="rm-success-detail-row" v-if="form.propAddress || form.propCity">
                    <span class="rm-success-detail-label">Property</span>
                    <span class="rm-success-detail-value">{{ [form.propAddress, form.propCity, form.propState].filter(Boolean).join(', ') }}</span>
                  </div>
                  <div class="rm-success-detail-row" v-if="form.loName">
                    <span class="rm-success-detail-label">Loan Officer</span>
                    <span class="rm-success-detail-value">{{ form.loName }}</span>
                  </div>
                </div>
                <button class="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base rm-success-close-btn" type="button" @click="closeTab">
                  Close
                </button>
              </div>
            </div>
          </transition>

          <!-- ── Form Panel ── -->
          <div class="application-form rm-application-form" v-if="!showSubmitted">

            <div class="app-header-container">
              <div class="loan-section-container">
                <div class="section-subsection">
                  <p class="loan-section">Reverse Mortgage<span>&nbsp;/&nbsp;</span></p>
                  <p class="loan-subsection">{{ currentSection.label }}</p>
                </div>
                <div class="progress-info">
                  <p class="step-info">Step {{ currentSectionIndex + 1 }} of {{ sections.length }}</p>
                  <p class="progress">{{ progressPct }}% complete</p>
                </div>
              </div>
              <p class="required-info"><span>*&nbsp;</span>Denotes a required field</p>
            </div>

            <div class="application-form-content">
              <form class="k-form k-form-md form-content">
                <fieldset class="k-form-fieldset">

                  <!-- ═══ SECTION 0: BORROWER ═══ -->
                  <template v-if="currentSectionIndex === 0">
                    <div class="form-row">
                      <div class="form-row-item pair">
                        <label class="rm-label">Full Legal Name <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.bwrFullName }]" v-model="form.bwrFullName" @blur="touch('bwrFullName')" />
                        <span class="rm-error" v-if="errors.bwrFullName">{{ errors.bwrFullName }}</span>
                      </div>
                      <div class="form-row-item pair">
                        <label class="rm-label">Gender</label>
                        <div class="yes-no-btns yes-no-btns-no-margin">
                          <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base yes-btn', { selected: form.bwrGender === 'female' }]" type="button" @click="form.bwrGender = 'female'">Female</button>
                          <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base yes-btn', { selected: form.bwrGender === 'male' }]" type="button" @click="form.bwrGender = 'male'">Male</button>
                        </div>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-item trio">
                        <label class="rm-label">Date of Birth <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.bwrDob }]" v-model="form.bwrDob" placeholder="MM/DD/YYYY" @input="formatDob('bwrDob')" @blur="touch('bwrDob')" maxlength="10" />
                        <span class="rm-error" v-if="errors.bwrDob">{{ errors.bwrDob }}</span>
                      </div>
                      <div class="form-row-item trio">
                        <label class="rm-label">Phone Number <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.bwrPhone }]" v-model="form.bwrPhone" placeholder="(000) 000-0000" @input="formatPhone('bwrPhone')" @blur="touch('bwrPhone')" maxlength="14" />
                        <span class="rm-error" v-if="errors.bwrPhone">{{ errors.bwrPhone }}</span>
                      </div>
                      <div class="form-row-item trio">
                        <label class="rm-label">Email Address</label>
                        <input :class="['rm-input', { invalid: errors.bwrEmail }]" type="email" v-model="form.bwrEmail" @blur="touch('bwrEmail')" />
                        <span class="rm-error" v-if="errors.bwrEmail">{{ errors.bwrEmail }}</span>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-item trio">
                        <label class="rm-label">Social Security # <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.bwrSsn }]" v-model="form.bwrSsn" placeholder="XXX-XX-XXXX" @input="formatSsn('bwrSsn')" @blur="touch('bwrSsn')" maxlength="11" />
                        <span class="rm-error" v-if="errors.bwrSsn">{{ errors.bwrSsn }}</span>
                      </div>
                      <div class="form-row-item trio">
                        <label class="rm-label">Total Monthly Income</label>
                        <input :class="['rm-input', { invalid: errors.bwrMonthlyIncome }]" v-model="form.bwrMonthlyIncome" placeholder="$0" @blur="formatCurrencyField('bwrMonthlyIncome')" />
                        <span class="rm-error" v-if="errors.bwrMonthlyIncome">{{ errors.bwrMonthlyIncome }}</span>
                      </div>
                      <div class="form-row-item trio">
                        <label class="rm-label">Available Assets</label>
                        <input :class="['rm-input', { invalid: errors.bwrAssets }]" v-model="form.bwrAssets" placeholder="$0" @blur="formatCurrencyField('bwrAssets')" />
                        <span class="rm-error" v-if="errors.bwrAssets">{{ errors.bwrAssets }}</span>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-item pair">
                        <label class="rm-label">Marital Status</label>
                        <select class="rm-input rm-select" v-model="form.bwrMaritalStatus">
                          <option value="">Select...</option><option>Married</option><option>Unmarried</option><option>Separated</option>
                        </select>
                      </div>
                      <div class="form-row-item pair">
                        <label class="rm-label">Ethnicity</label>
                        <input class="rm-input" v-model="form.bwrEthnicity" />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-item pair">
                        <label class="rm-label">Race</label>
                        <input class="rm-input" v-model="form.bwrRace" />
                      </div>
                    </div>

                    <div v-for="f in bwrYesNoFields" :key="f.model" class="form-row yes-no">
                      <div class="form-row-item yes-no">
                        <label class="rm-label">{{ f.label }}</label>
                        <div class="yes-no-btns">
                          <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base yes-btn', { selected: form[f.model] === true }]" type="button" @click="form[f.model] = true">Yes</button>
                          <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base no-btn', { selected: form[f.model] === false }]" type="button" @click="form[f.model] = false">No</button>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- ═══ SECTION 1: CO-BORROWER ═══ -->
                  <template v-if="currentSectionIndex === 1">
                    <div class="form-row yes-no">
                      <div class="form-row-item yes-no">
                        <label class="rm-label">Will there be a co-borrower? <span class="required-indicator">*</span></label>
                        <div class="yes-no-btns">
                          <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base yes-btn', { selected: form.hasCoborrower === true }]" type="button" @click="form.hasCoborrower = true">Yes</button>
                          <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base no-btn', { selected: form.hasCoborrower === false }]" type="button" @click="form.hasCoborrower = false">No</button>
                        </div>
                      </div>
                    </div>

                    <template v-if="form.hasCoborrower === true">
                      <div class="form-row"><div class="form-row-item"><p class="rm-section-note">If borrower is married and spouse resides in subject property, spouse info is required.</p></div></div>

                      <div class="form-row">
                        <div class="form-row-item pair">
                          <label class="rm-label">Full Legal Name <span class="required-indicator">*</span></label>
                          <input :class="['rm-input', { invalid: errors.cobwrFullName }]" v-model="form.cobwrFullName" @blur="touch('cobwrFullName')" />
                          <span class="rm-error" v-if="errors.cobwrFullName">{{ errors.cobwrFullName }}</span>
                        </div>
                        <div class="form-row-item pair">
                          <label class="rm-label">Date of Birth <span class="required-indicator">*</span></label>
                          <input :class="['rm-input', { invalid: errors.cobwrDob }]" v-model="form.cobwrDob" placeholder="MM/DD/YYYY" @input="formatDob('cobwrDob')" @blur="touch('cobwrDob')" maxlength="10" />
                          <span class="rm-error" v-if="errors.cobwrDob">{{ errors.cobwrDob }}</span>
                        </div>
                      </div>

                      <div class="form-row">
                        <div class="form-row-item trio">
                          <label class="rm-label">Phone Number <span class="required-indicator">*</span></label>
                          <input :class="['rm-input', { invalid: errors.cobwrPhone }]" v-model="form.cobwrPhone" placeholder="(000) 000-0000" @input="formatPhone('cobwrPhone')" @blur="touch('cobwrPhone')" maxlength="14" />
                          <span class="rm-error" v-if="errors.cobwrPhone">{{ errors.cobwrPhone }}</span>
                        </div>
                        <div class="form-row-item trio">
                          <label class="rm-label">Email Address</label>
                          <input :class="['rm-input', { invalid: errors.cobwrEmail }]" type="email" v-model="form.cobwrEmail" @blur="touch('cobwrEmail')" />
                          <span class="rm-error" v-if="errors.cobwrEmail">{{ errors.cobwrEmail }}</span>
                        </div>
                        <div class="form-row-item trio">
                          <label class="rm-label">Social Security # <span class="required-indicator">*</span></label>
                          <input :class="['rm-input', { invalid: errors.cobwrSsn }]" v-model="form.cobwrSsn" placeholder="XXX-XX-XXXX" @input="formatSsn('cobwrSsn')" @blur="touch('cobwrSsn')" maxlength="11" />
                          <span class="rm-error" v-if="errors.cobwrSsn">{{ errors.cobwrSsn }}</span>
                        </div>
                      </div>

                      <div class="form-row">
                        <div class="form-row-item pair">
                          <label class="rm-label">Total Monthly Income</label>
                          <input :class="['rm-input', { invalid: errors.cobwrMonthlyIncome }]" v-model="form.cobwrMonthlyIncome" placeholder="$0" @blur="formatCurrencyField('cobwrMonthlyIncome')" />
                          <span class="rm-error" v-if="errors.cobwrMonthlyIncome">{{ errors.cobwrMonthlyIncome }}</span>
                        </div>
                        <div class="form-row-item pair">
                          <label class="rm-label">Available Assets</label>
                          <input :class="['rm-input', { invalid: errors.cobwrAssets }]" v-model="form.cobwrAssets" placeholder="$0" @blur="formatCurrencyField('cobwrAssets')" />
                          <span class="rm-error" v-if="errors.cobwrAssets">{{ errors.cobwrAssets }}</span>
                        </div>
                      </div>

                      <div class="form-row">
                        <div class="form-row-item pair">
                          <label class="rm-label">Marital Status</label>
                          <select class="rm-input rm-select" v-model="form.cobwrMaritalStatus">
                            <option value="">Select...</option><option>Married</option><option>Unmarried</option><option>Separated</option>
                          </select>
                        </div>
                        <div class="form-row-item pair">
                          <label class="rm-label">Ethnicity</label>
                          <input class="rm-input" v-model="form.cobwrEthnicity" />
                        </div>
                      </div>

                      <div class="form-row"><div class="form-row-item pair"><label class="rm-label">Race</label><input class="rm-input" v-model="form.cobwrRace" /></div></div>

                      <div v-for="f in cobwrYesNoFields" :key="'cy-'+f.model" class="form-row yes-no">
                        <div class="form-row-item yes-no">
                          <label class="rm-label">{{ f.label }}</label>
                          <div class="yes-no-btns">
                            <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base yes-btn', { selected: form[f.model] === true }]" type="button" @click="form[f.model] = true">Yes</button>
                            <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base no-btn', { selected: form[f.model] === false }]" type="button" @click="form[f.model] = false">No</button>
                          </div>
                        </div>
                      </div>
                    </template>

                    <div v-if="form.hasCoborrower === false" class="rm-no-coborrower-msg">
                      <i class="fa-regular fa-circle-info"></i> No co-borrower — click <strong>Continue</strong> to proceed to the property section.
                    </div>
                  </template>

                  <!-- ═══ SECTION 2: PROPERTY ═══ -->
                  <template v-if="currentSectionIndex === 2">
                    <!-- Purpose of Loan + Property Type moved here -->
                    <div class="form-row">
                      <div class="form-row-item pair">
                        <label class="rm-label">Purpose of Loan <span class="required-indicator">*</span></label>
                        <select :class="['rm-input rm-select', { invalid: errors.purposeOfLoan }]" v-model="form.purposeOfLoan" @blur="touch('purposeOfLoan')">
                          <option value="">Select...</option>
                          <option>Purchase</option>
                          <option>Refinance</option>
                        </select>
                        <span class="rm-error" v-if="errors.purposeOfLoan">{{ errors.purposeOfLoan }}</span>
                      </div>
                      <div class="form-row-item pair">
                        <label class="rm-label">Property Type</label>
                        <select class="rm-input rm-select" v-model="form.propertyType">
                          <option value="">Select...</option>
                          <option>Single Family</option><option>Condo</option><option>PUD</option><option>Manufactured Home</option><option>2-4 Unit</option>
                        </select>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-item"><label class="rm-label">Street Address</label><input class="rm-input" v-model="form.propAddress" /></div>
                    </div>
                    <div class="form-row">
                      <div class="form-row-item trio"><label class="rm-label">City</label><input class="rm-input" v-model="form.propCity" /></div>
                      <div class="form-row-item trio">
                        <label class="rm-label">State</label>
                        <input :class="['rm-input', { invalid: errors.propState }]" v-model="form.propState" maxlength="2" @input="form.propState = form.propState.toUpperCase()" @blur="touch('propState')" />
                        <span class="rm-error" v-if="errors.propState">{{ errors.propState }}</span>
                      </div>
                      <div class="form-row-item trio">
                        <label class="rm-label">ZIP Code <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.propZip }]" v-model="form.propZip" maxlength="10" @input="formatZip('propZip')" @blur="touch('propZip')" />
                        <span class="rm-error" v-if="errors.propZip">{{ errors.propZip }}</span>
                      </div>
                    </div>
                    <div class="form-row">
                      <div class="form-row-item pair">
                        <label class="rm-label">Estimate of Property Value <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.propEstValue }]" v-model="form.propEstValue" placeholder="$0" @blur="formatCurrencyField('propEstValue'); touch('propEstValue')" />
                        <span class="rm-error" v-if="errors.propEstValue">{{ errors.propEstValue }}</span>
                      </div>
                      <div class="form-row-item pair">
                        <label class="rm-label">Current Mortgage Payoff Amt <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.propMortgagePayoff }]" v-model="form.propMortgagePayoff" placeholder="$0" @blur="formatCurrencyField('propMortgagePayoff'); touch('propMortgagePayoff')" />
                        <span class="rm-error" v-if="errors.propMortgagePayoff">{{ errors.propMortgagePayoff }}</span>
                      </div>
                    </div>
                    <div class="form-row">
                      <div class="form-row-item pair"><label class="rm-label">Property Tax / Mo.</label><input class="rm-input" v-model="form.propTaxMo" placeholder="$0" @blur="formatCurrencyField('propTaxMo')" /></div>
                      <div class="form-row-item pair"><label class="rm-label">Homeowners Insurance / Mo.</label><input class="rm-input" v-model="form.propHoiMo" placeholder="$0" @blur="formatCurrencyField('propHoiMo')" /></div>
                    </div>
                    <div class="form-row">
                      <div class="form-row-item pair"><label class="rm-label">HOA Amount / Mo.</label><input class="rm-input" v-model="form.propHoaMo" placeholder="$0" @blur="formatCurrencyField('propHoaMo')" /></div>
                      <div class="form-row-item pair"><label class="rm-label">Min Debt Payments / Mo.</label><input class="rm-input" v-model="form.propMinDebtMo" placeholder="$0" @blur="formatCurrencyField('propMinDebtMo')" /></div>
                    </div>
                    <div class="form-row">
                      <div class="form-row-item pair">
                        <label class="rm-label">Years at Present Address</label>
                        <input :class="['rm-input', { invalid: errors.propYearsAtAddress }]" v-model="form.propYearsAtAddress" @input="numbersOnly('propYearsAtAddress')" @blur="touch('propYearsAtAddress')" maxlength="2" />
                        <span class="rm-error" v-if="errors.propYearsAtAddress">{{ errors.propYearsAtAddress }}</span>
                      </div>
                      <div class="form-row-item pair">
                        <label class="rm-label"># of Adults 18+ in Home</label>
                        <input :class="['rm-input', { invalid: errors.propAdultsInHome }]" type="number" min="0" max="20" v-model="form.propAdultsInHome" @blur="touch('propAdultsInHome')" />
                        <span class="rm-error" v-if="errors.propAdultsInHome">{{ errors.propAdultsInHome }}</span>
                      </div>
                    </div>
                    <div class="form-row">
                      <div class="form-row-item pair"><label class="rm-label">Add'l Lien Payoff Amount</label><input class="rm-input" v-model="form.propAddlLienPayoff" placeholder="$0" @blur="formatCurrencyField('propAddlLienPayoff')" /></div>
                    </div>
                    <div v-for="f in propDeclarations" :key="'pd-'+f.model" class="form-row yes-no">
                      <div class="form-row-item yes-no">
                        <label class="rm-label">{{ f.label }}</label>
                        <div class="yes-no-btns">
                          <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base yes-btn', { selected: form[f.model] === true }]" type="button" @click="form[f.model] = true">Yes</button>
                          <button :class="['k-button k-button-md k-rounded-md k-button-solid k-button-solid-base no-btn', { selected: form[f.model] === false }]" type="button" @click="form[f.model] = false">No</button>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- ═══ SECTION 3: LO INFORMATION (moved to end) ═══ -->
                  <template v-if="currentSectionIndex === 3">
                    <div class="form-row">
                      <div class="form-row-item pair">
                        <label class="rm-label">LO Name <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.loName }]" v-model="form.loName" @blur="touch('loName')" />
                        <span class="rm-error" v-if="errors.loName">{{ errors.loName }}</span>
                      </div>
                      <div class="form-row-item pair">
                        <label class="rm-label">LO NMLS ID <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.loNmlsId }]" v-model="form.loNmlsId" placeholder="NMLS#" @blur="touch('loNmlsId')" />
                        <span class="rm-error" v-if="errors.loNmlsId">{{ errors.loNmlsId }}</span>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-item pair">
                        <label class="rm-label">LO Email Address <span class="required-indicator">*</span></label>
                        <input :class="['rm-input', { invalid: errors.loEmail }]" type="email" v-model="form.loEmail" @blur="touch('loEmail')" />
                        <span class="rm-error" v-if="errors.loEmail">{{ errors.loEmail }}</span>
                      </div>
                      <div class="form-row-item pair">
                        <label class="rm-label">LO Cell Phone</label>
                        <input :class="['rm-input', { invalid: errors.loCellPhone }]" v-model="form.loCellPhone" placeholder="(000) 000-0000" @input="formatPhone('loCellPhone')" @blur="touch('loCellPhone')" maxlength="14" />
                        <span class="rm-error" v-if="errors.loCellPhone">{{ errors.loCellPhone }}</span>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-row-item pair">
                        <label class="rm-label">LO Office Phone</label>
                        <input :class="['rm-input', { invalid: errors.loOfficePhone }]" v-model="form.loOfficePhone" placeholder="(000) 000-0000" @input="formatPhone('loOfficePhone')" @blur="touch('loOfficePhone')" maxlength="14" />
                        <span class="rm-error" v-if="errors.loOfficePhone">{{ errors.loOfficePhone }}</span>
                      </div>
                    </div>
                  </template>

                </fieldset>

                <transition name="rm-slide">
                  <div class="rm-error-summary" v-if="showValidationSummary">
                    <i class="fa-solid fa-circle-exclamation"></i>
                    Please fix the errors above before continuing.
                  </div>
                </transition>

                <div :class="['k-form-buttons', { 'first-section': currentSectionIndex === 0 }]">
                  <button v-if="currentSectionIndex > 0" class="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base back-btn" type="button" @click="goBack">Back</button>
                  <button v-if="currentSectionIndex < sections.length - 1" class="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base next-btn" type="button" @click="goNext">Continue</button>
                  <button v-if="currentSectionIndex === sections.length - 1" class="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base next-btn" type="button" :disabled="isSaving" @click="onSave">
                    <i class="fa-solid fa-spinner fa-spin" v-if="isSaving"></i>
                    {{ isSaving ? 'Submitting...' : 'Submit Request' }}
                  </button>
                </div>

              </form>
            </div>
          </div><!-- /v-if form -->

          <!-- ── Side Menu ── -->
          <div class="side-menu-container" v-if="!showSubmitted">
            <div class="side-panels">
              <div v-for="(section, index) in sections" :key="section.id" class="panel-container" @click="goToSection(index)">
                <div :class="['k-expander menu-panel', { 'active-section': currentSectionIndex === index }]">
                  <div role="button" class="k-expander-header">
                    <div class="k-expander-title">
                      {{ section.label }}
                      <i :class="['menu-icon', index < currentSectionIndex ? 'fa-light fa-circle-check completed' : index === currentSectionIndex ? 'fa-light fa-circle-check' : 'fa-light fa-lock']" aria-hidden="true"></i>
                    </div>
                    <span class="k-spacer"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <transition name="toast-slide">
      <div class="rm-toast rm-toast-error" v-if="showSaveError"><i class="fa-duotone fa-solid fa-circle-exclamation"></i> {{ saveErrorMsg }}</div>
    </transition>
  </div>
</template>

<script>
import auth from "../authConfig.js"
import apiService from "../api/api.service.js"

const RULES = {
  loName:            { required: true,  label: "LO Name" },
  loNmlsId:          { required: true,  label: "LO NMLS ID", pattern: /^\d+$/, patternMsg: "NMLS ID must be numeric" },
  loEmail:           { required: true,  label: "LO Email", email: true },
  loCellPhone:       { required: false, label: "LO Cell Phone", phone: true },
  loOfficePhone:     { required: false, label: "LO Office Phone", phone: true },
  bwrFullName:       { required: true,  label: "Full Legal Name" },
  bwrDob:            { required: true,  label: "Date of Birth", dob: true },
  bwrPhone:          { required: true,  label: "Phone Number", phone: true },
  bwrEmail:          { required: false, label: "Email Address", email: true },
  bwrSsn:            { required: true,  label: "Social Security #", ssn: true },
  bwrMonthlyIncome:  { required: false, label: "Total Monthly Income", currency: true },
  bwrAssets:         { required: false, label: "Available Assets", currency: true },
  purposeOfLoan:     { required: true,  label: "Purpose of Loan" },
  // Co-borrower rules — required status is dynamic based on hasCoborrower toggle
  cobwrFullName:     { required: false, label: "Co-Borrower Full Name" },
  cobwrDob:          { required: false, label: "Co-Borrower Date of Birth", dob: true },
  cobwrPhone:        { required: false, label: "Co-Borrower Phone", phone: true },
  cobwrEmail:        { required: false, label: "Co-Borrower Email", email: true },
  cobwrSsn:          { required: false, label: "Co-Borrower SSN", ssn: true },
  cobwrMonthlyIncome:{ required: false, label: "Co-Borrower Monthly Income", currency: true },
  cobwrAssets:       { required: false, label: "Co-Borrower Assets", currency: true },
  propZip:           { required: true,  label: "ZIP Code", zip: true },
  propState:         { required: false, label: "State", pattern: /^[A-Za-z]{2}$/, patternMsg: "Enter a 2-letter state code" },
  propEstValue:      { required: true,  label: "Estimate of Property Value", currency: true },
  propMortgagePayoff:{ required: true,  label: "Current Mortgage Payoff Amt", currency: true },
  propYearsAtAddress:{ required: false, label: "Years at Present Address", positiveNum: true },
  propAdultsInHome:  { required: false, label: "# of Adults 18+", positiveNum: true },
}

// Section 1 (co-borrower) fields are validated dynamically — see validateSection()
const SECTION_FIELDS = [
  ["bwrFullName", "bwrDob", "bwrPhone", "bwrEmail", "bwrSsn", "bwrMonthlyIncome", "bwrAssets"],
  ["cobwrFullName", "cobwrDob", "cobwrPhone", "cobwrEmail", "cobwrSsn", "cobwrMonthlyIncome", "cobwrAssets"],
  ["purposeOfLoan", "propZip", "propState", "propEstValue", "propMortgagePayoff", "propYearsAtAddress", "propAdultsInHome"],
  ["loName", "loNmlsId", "loEmail", "loCellPhone", "loOfficePhone"],
]

function validateField(field, value, overrideRequired) {
  const rule = RULES[field]
  if (!rule) return null
  const v = (value ?? "").toString().trim()
  const isRequired = overrideRequired !== undefined ? overrideRequired : rule.required
  if (isRequired && !v) return `${rule.label} is required`
  if (!v) return null
  if (rule.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Enter a valid email address"
  if (rule.phone && v.replace(/\D/g, "").length !== 10) return "Phone must be 10 digits"
  if (rule.ssn && !/^\d{3}-\d{2}-\d{4}$/.test(v)) return "SSN must be in XXX-XX-XXXX format"
  if (rule.zip && !/^\d{5}(-\d{4})?$/.test(v)) return "Enter a valid ZIP (e.g. 48105 or 48105-1234)"
  if (rule.currency) {
    const stripped = v.replace(/[$,]/g, "")
    if (isNaN(parseFloat(stripped)) || parseFloat(stripped) < 0) return "Enter a valid dollar amount"
  }
  if (rule.positiveNum && (isNaN(Number(v)) || Number(v) < 0)) return "Must be a positive number"
  if (rule.dob) {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return "Enter date as MM/DD/YYYY"
    const [mm, dd, yyyy] = v.split("/").map(Number)
    const d = new Date(yyyy, mm - 1, dd)
    if (d.getMonth() !== mm - 1 || d.getDate() !== dd || d.getFullYear() !== yyyy) return "Enter a valid date"
    const age = (new Date() - d) / (1000 * 60 * 60 * 24 * 365.25)
    if (age < 0) return "Date of birth cannot be in the future"
    if (age < 18) return "Borrower must be at least 18 years old"
    if (age > 120) return "Enter a valid date of birth"
  }
  if (rule.pattern && !rule.pattern.test(v)) return rule.patternMsg
  return null
}

export default {
  name: "ReverseMortgageView",

  data() {
    return {
      currentSectionIndex: 0,
      isSaving: false,
      showSubmitted: false,
      showSaveError: false,
      saveErrorMsg: "",
      showValidationSummary: false,
      errors: {},
      touched: {},

      // LO profile for header display
      loProfile: { name: "", phone: "", photo: "" },

      sections: [
        { id: "bwr",   label: "Borrower" },
        { id: "cobwr", label: "Co-Borrower" },
        { id: "prop",  label: "Property" },
        { id: "lo",    label: "LO Information" },
      ],

      // Borrower yes/no — removed Existing FHA Loan + Power of Attorney
      bwrYesNoFields: [
        { label: "US Citizen?",                        model: "bwrUsCitizen" },
        { label: "Permanent Resident Alien?",          model: "bwrPermResident" },
        { label: "Intend to buy a financial product?", model: "bwrBuyFinancial" },
      ],
      // Borrower declarations — REMOVED entirely per request
      // Co-borrower yes/no — same removals as borrower
      cobwrYesNoFields: [
        { label: "US Citizen?",                        model: "cobwrUsCitizen" },
        { label: "Permanent Resident Alien?",          model: "cobwrPermResident" },
        { label: "Intend to buy a financial product?", model: "cobwrBuyFinancial" },
      ],
      // Co-borrower declarations — REMOVED entirely per request
      propDeclarations: [
        { label: "Is this the primary residence?",                  model: "propPrimaryRes" },
        { label: "Property held in trust?",                         model: "propInTrust" },
        { label: "Refi in past 12 mos & received $500+ cash out?",  model: "propRefiCashOut" },
        { label: "Borrower owns additional properties?",            model: "propOwnsAddl" },
        { label: "Taxes paid on time (past 24 months)?",            model: "propTaxesCurrent" },
        { label: "HOI paid on time (past 12 months)?",              model: "propHoiCurrent" },
        { label: "HOA paid on time (past 12 months)?",              model: "propHoaCurrent" },
        { label: "Does the home have a solar lease?",               model: "propSolarLease" },
      ],

      form: {
        packagePricing: false, packageApplication: false, attachTriMerge: false,
        loName: "", loNmlsId: "", loEmail: "",
        loCellPhone: "", loOfficePhone: "",
        purposeOfLoan: "", propertyType: "",
        bwrFullName: "", bwrGender: "", bwrDob: "", bwrPhone: "", bwrEmail: "",
        bwrSsn: "", bwrMonthlyIncome: "", bwrAssets: "",
        bwrUsCitizen: null, bwrPermResident: null, bwrMaritalStatus: "",
        bwrEthnicity: "", bwrRace: "",
        bwrBuyFinancial: null,
        hasCoborrower: null,
        cobwrFullName: "", cobwrDob: "", cobwrPhone: "", cobwrEmail: "",
        cobwrSsn: "", cobwrMonthlyIncome: "", cobwrAssets: "",
        cobwrUsCitizen: null, cobwrPermResident: null, cobwrMaritalStatus: "",
        cobwrEthnicity: "", cobwrRace: "",
        cobwrBuyFinancial: null,
        propAddress: "", propCity: "", propState: "", propZip: "",
        propEstValue: "", propPrimaryRes: null, propMortgagePayoff: "",
        propTaxMo: "", propHoiMo: "", propHoaMo: "", propMinDebtMo: "",
        propYearsAtAddress: "", propAdultsInHome: "", propAddlLienPayoff: "",
        propInTrust: null, propRefiCashOut: null, propOwnsAddl: null,
        propTaxesCurrent: null, propHoiCurrent: null, propHoaCurrent: null, propSolarLease: null,
      }
    }
  },

  computed: {
    currentSection() { return this.sections[this.currentSectionIndex] },
    progressPct() { return Math.round((this.currentSectionIndex / this.sections.length) * 100) }
  },

  async mounted() {
    document.title = 'Application - Reverse'
    await this.loadUserData()
  },

  methods: {
    // ── Load logged-in user and pre-fill LO fields ───────────────────────────
    async loadUserData() {
      try {
        const token = await auth.acquireTokenResponse()
        const azureId = localStorage.getItem("azure_id") || ""
        if (!azureId || !token?.accessToken) return

        const result = await apiService.GetUsersAzureData(azureId, token.accessToken)
        const user = result?.data?.data
        if (!user) return

        // Fill LO form fields from azure data
        const nmls = user?.onPremisesExtensionAttributes?.extensionAttribute11 ?? ""
        const name = user?.displayName ?? ""
        const email = user?.mail ?? user?.userPrincipalName ?? ""
        const mobile = user?.mobilephone ?? ""
        const office = (user?.businessphones && user.businessphones[0]) ?? ""

        this.form.loName       = name
        this.form.loNmlsId     = nmls
        this.form.loEmail      = email
        if (mobile) {
          this.form.loCellPhone = mobile
          this.formatPhone('loCellPhone')
        }
        if (office) {
          this.form.loOfficePhone = office
          this.formatPhone('loOfficePhone')
        }

        // Header display
        this.loProfile.name  = name
        this.loProfile.phone = mobile || (user?.businessphones?.[0] ?? "")

        // Try to load profile photo
        try {
          const photoRes = await apiService.GetUserProfileImage(azureId, token.accessToken)
          const img = photoRes?.data?.data
          if (img) this.loProfile.photo = img
        } catch { /* photo is optional */ }

      } catch { /* fail silently — user can fill manually */ }
    },

    closeTab() {
      window.close()
      // Fallback if window.close() is blocked
      this.$router.back()
    },

    touch(field) {
      this.touched = { ...this.touched, [field]: true }
      // For co-borrower fields, dynamically set required based on toggle
      const cobwrRequired = this.form.hasCoborrower === true
      const cobwrFields = ["cobwrFullName", "cobwrDob", "cobwrPhone", "cobwrSsn"]
      const overrideRequired = cobwrFields.includes(field) ? cobwrRequired : undefined
      const err = validateField(field, this.form[field], overrideRequired)
      this.errors = { ...this.errors, [field]: err }
    },

    validateSection(index) {
      const fields = SECTION_FIELDS[index] || []
      const newErrors = { ...this.errors }
      const newTouched = { ...this.touched }
      let valid = true

      // Co-borrower section — skip validation if no co-borrower
      if (index === 1 && this.form.hasCoborrower !== true) {
        // Just need hasCoborrower to be answered
        if (this.form.hasCoborrower === null) {
          // Not answered yet — flag it
          this.showValidationSummary = true
          return false
        }
        return true
      }

      // Co-borrower required fields when hasCoborrower is true
      const cobwrRequiredFields = ["cobwrFullName", "cobwrDob", "cobwrPhone", "cobwrSsn"]

      fields.forEach(field => {
        const cobwrRequired = (index === 1 && this.form.hasCoborrower === true && cobwrRequiredFields.includes(field))
        const overrideRequired = cobwrRequired ? true : undefined
        const err = validateField(field, this.form[field], overrideRequired)
        newErrors[field] = err
        newTouched[field] = true
        if (err) valid = false
      })
      this.errors = newErrors
      this.touched = newTouched
      return valid
    },

    goNext() {
      this.showValidationSummary = false
      const valid = this.validateSection(this.currentSectionIndex)
      if (!valid) {
        this.showValidationSummary = true
        this.$nextTick(() => {
          const el = document.querySelector('.rm-input.invalid, .rm-error')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
        return
      }
      if (this.currentSectionIndex < this.sections.length - 1) {
        this.currentSectionIndex++
        this.$nextTick(() => { const el = document.querySelector('.application-form-content'); if (el) el.scrollTop = 0 })
      }
    },

    goBack() {
      this.showValidationSummary = false
      if (this.currentSectionIndex > 0) {
        this.currentSectionIndex--
        this.$nextTick(() => { const el = document.querySelector('.application-form-content'); if (el) el.scrollTop = 0 })
      }
    },

    goToSection(index) {
      if (index <= this.currentSectionIndex) {
        this.showValidationSummary = false
        this.currentSectionIndex = index
        this.$nextTick(() => { const el = document.querySelector('.application-form-content'); if (el) el.scrollTop = 0 })
      }
    },

    formatPhone(field) {
      let digits = (this.form[field] || "").replace(/\D/g, "").slice(0, 10)
      if (digits.length <= 3)      this.form[field] = digits.length ? `(${digits}` : ""
      else if (digits.length <= 6) this.form[field] = `(${digits.slice(0, 3)}) ${digits.slice(3)}`
      else                          this.form[field] = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    },

    formatSsn(field) {
      let digits = (this.form[field] || "").replace(/\D/g, "").slice(0, 9)
      if (digits.length <= 3)      this.form[field] = digits
      else if (digits.length <= 5) this.form[field] = `${digits.slice(0, 3)}-${digits.slice(3)}`
      else                          this.form[field] = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
    },

    formatDob(field) {
      let digits = (this.form[field] || "").replace(/\D/g, "").slice(0, 8)
      if (digits.length <= 2)      this.form[field] = digits
      else if (digits.length <= 4) this.form[field] = `${digits.slice(0, 2)}/${digits.slice(2)}`
      else                          this.form[field] = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
    },

    formatZip(field) {
      let digits = (this.form[field] || "").replace(/\D/g, "").slice(0, 9)
      this.form[field] = digits.length <= 5 ? digits : `${digits.slice(0, 5)}-${digits.slice(5)}`
    },

    numbersOnly(field) {
      this.form[field] = (this.form[field] || "").replace(/[^\d]/g, "")
    },

    formatCurrencyField(field) {
      const raw = (this.form[field] || "").toString().replace(/[$,\s]/g, "")
      if (!raw || isNaN(parseFloat(raw))) return
      this.form[field] = "$" + parseFloat(raw).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },

    async onSave() {
      this.showValidationSummary = false
      const valid = this.validateSection(this.currentSectionIndex)
      if (!valid) { this.showValidationSummary = true; return }

      this.isSaving = true
      this.showSaveError = false

      try {
        const token = await auth.acquireTokenResponse()
        const azureId = localStorage.getItem("azure_id") || ""
        const userData = JSON.parse(localStorage.getItem("user_azure_data")) || "";
        // ── Sanitize types before sending to API ──────────────────────────
        const toNullableInt = v => {
          const n = parseInt(v)
          return isNaN(n) ? null : n
        }
        const dto = {
          ...this.form,
          // int? — send null not ""
          propAdultsInHome: toNullableInt(this.form.propAdultsInHome),
          // metadata
          submittedByAzureId: azureId,
          OrgId:userData.extensionAttribute4,
          submittedAt: new Date().toISOString()
        }
        const response = await apiService.CreateReverseMortgageRequest(dto, token.accessToken)
        if (!response.data?.issuccess) throw new Error(response.data?.message || "Server returned an error.")
        this.showSubmitted = true
      } catch (err) {
        this.saveErrorMsg = err.message || "An error occurred. Please try again."
        this.showSaveError = true
        setTimeout(() => { this.showSaveError = false }, 5000)
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>

<style scoped>
/*
  Swift SPA: html { font-size: 62.5% } → 1rem = 10px
  1.4rem = 14px, 1.6rem = 16px, 1.7rem = 17px
*/

/* ── Full page wrapper ── */
.rm-full-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-dkr, #e4e7ea);
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

/* ── Header — matches applications project appHeader.css exactly ── */
.app-header {
  display: flex;
  padding: 15px 35px;
  align-items: center;
  background: var(--background-dk);
  width: 100%;
  justify-content: center;
  border-bottom: 1px solid var(--con-primary-ltr);
  box-sizing: border-box;
}

.app-header-content {
  width: 100%;
  max-width: 1400px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dba-logo-container .dba-logo { height: 85px; }

.lo-info-container { display: flex; align-items: center; }

.lo-info-container .lo-text {
  display: flex;
  flex-direction: column;
  text-align: right;
  margin-right: 10px;
}

.lo-info-container .lo-name {
  font-weight: 600;
  font-size: 1.6rem;
  color: var(--on-background);
}

.lo-info-container .lo-mobile.link a {
  color: var(--on-background);
  font-size: 1.5rem;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: var(--con-primary-lt);
}

.lo-info-container .lo-mobile.link a:hover { color: var(--con-primary-dk); }

.lo-img-container { height: 85px; }

.lo-img-container .lo-img {
  height: 85px;
  width: 85px;
  object-fit: cover;
  border-radius: 50%;
}

/* ── Home view wrapper ── */
.home-view.rm-home-view {
  width: 100%;
  flex-grow: 1;
  padding: 35px;
  background-color: var(--background-dkr, #e4e7ea);
  display: flex;
  justify-content: center;
  overflow: hidden;
  min-height: 0;
}

.home-content {
  width: 100%;
  max-width: 1400px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.form-container {
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  min-height: 0;
}

/* ── Main form panel ── */
.rm-application-form {
  width: 70%;
  display: flex;
  flex-direction: column;
  background-color: var(--background-dk);
  padding: 45px 35px 35px 35px;
  border-radius: 12px;
  height: 100%;
}

/* ── Form header — exact match to homeView.css ── */
.app-header-container {
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
  width: 100%;
}

.loan-section-container {
  display: flex;
  font-size: 2.72rem;  /* 27.2px — matches applications screenshot */
  font-weight: 600;
  width: 100%;
  align-items: center;
}

.section-subsection { display: flex; }
.loan-section  { color: var(--con-primary-lt); }   /* blue, matches the app */
.loan-subsection { color: var(--on-background); }   /* dark, matches the app */

.progress-info {
  font-size: 1.4rem;
  text-align: right;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  line-height: 1.4;
}

.progress-info .step-info { color: var(--secondary-dkr); font-size: 1.4rem; }
.progress-info .progress  { color: var(--text-secondary-on-background); }

.required-info { font-size: 1.4rem; font-style: italic; margin-top: 5px; }
.required-info span { color: red; }

/* ── Scrollable content ── */
.application-form-content {
  width: 100%;
  flex-grow: 1;
  overflow-y: auto;
  padding-right: 15px;
}

.application-form-content::-webkit-scrollbar { width: 4px; }
.application-form-content::-webkit-scrollbar-thumb { background: var(--secondary-ltr); border-radius: 4px; }

.form-content {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  font-size: 1.6rem; /* 16px body */
}

.k-form-fieldset { flex-grow: 1; position: relative; }

/* Packages & notices */
.rm-pkg-row { display: flex; flex-wrap: wrap; gap: 18px; margin-bottom: 14px; }
.rm-pkg-check { display: flex; align-items: center; gap: 7px; font-size: 1.6rem; cursor: pointer; color: var(--on-background); }
.rm-pkg-check input { accent-color: var(--primary); cursor: pointer; width: 15px; height: 15px; }
.rm-notices { margin-bottom: 20px; display: flex; flex-direction: column; gap: 5px; }
.rm-notice-text { font-size: 1.5rem; color: var(--secondary-dkr); font-style: italic; }
.rm-notice-text i { margin-right: 4px; }
.rm-notice-text.warning { color: var(--on-background); font-style: normal; }
.rm-section-note { font-size: 1.6rem; color: var(--secondary-dkr); font-style: italic; margin-bottom: 16px; line-height: 1.5; }

/* Form rows */
.form-row { width: 100%; display: flex; }
.form-row.subheading { margin-top: 8px; margin-bottom: 4px; }
.form-row.subheading .subheading-text { font-size: 1.8rem; font-weight: 600; color: var(--on-background); }
.form-row.yes-no { margin-bottom: 0; }
.form-row-item { width: 100%; margin-bottom: 1.2rem; margin-left: .5%; margin-right: .5%; display: flex; flex-direction: column; }
.form-row-item.yes-no { margin-bottom: 1.3rem; }
.form-row-item.pair { width: 49%; }
.form-row-item.trio { width: 33%; }

/* Labels & inputs */
.rm-label { font-size: 1.6rem; color: var(--secondary-dkst); margin-bottom: 5px; margin-top: 12px; display: block; }
.form-row.yes-no .rm-label { color: var(--on-background); font-size: 1.6rem; }
.required-indicator { color: red; }

.rm-input {
  width: 100%;
  height: 38px;
  border: 1px solid var(--secondary-ltr);
  border-radius: 4px;
  padding: 0 10px;
  font-size: 1.6rem;
  color: var(--on-background);
  background: var(--background);
  outline: none;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  transition: border-color 0.15s, box-shadow 0.15s;
  display: block;
}

.rm-input:focus, .rm-input:focus-visible { border-color: #00000029 !important; box-shadow: 0 0 0 2px #00000014 !important; outline: none !important; }
.rm-input.invalid { border-color: var(--error, #993333); background-color: var(--error-ltr, #f9ecec); }
.rm-input.invalid:focus { box-shadow: 0 0 0 2px rgba(153, 51, 51, 0.12); }

.rm-error { font-size: 1.4rem; color: var(--error, #993333); margin-top: 3px; display: flex; align-items: center; gap: 4px; }

.rm-error-summary {
  background-color: var(--error-lt, #ecc6c6);
  border: 1px solid var(--error, #993333);
  color: var(--error, #993333);
  font-size: 1.6rem;
  font-weight: 600;
  padding: 10px 14px;
  border-radius: 6px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rm-slide-enter-active, .rm-slide-leave-active { transition: all 0.2s ease; overflow: hidden; }
.rm-slide-enter-from, .rm-slide-leave-to { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; margin-top: 0; }
.rm-slide-enter-to, .rm-slide-leave-from { opacity: 1; max-height: 60px; }

.rm-select { cursor: pointer; appearance: auto; }

/* Yes/No buttons */

.yes-no-btns { margin-top: 8px; }
.yes-no-btns .k-button { min-width: 150px; margin-right: 10px; background-color: var(--background); border: 1px solid var(--primary); font-size: 1.6rem; color: var(--primary); cursor: pointer; padding: 6px 10px; }
.yes-no-btns .yes-btn.selected { background-color: var(--con-primary-ltr); border-color: var(--con-primary-dk); color: var(--con-primary-dk); font-weight: 600; }
.yes-no-btns .no-btn.selected  { background-color: var(--error-lt); border-color: var(--error); color: var(--error); font-weight: 600; }
.yes-no-btns-no-margin{margin-top:0px}
/* Nav buttons */
.k-form-buttons { position: sticky; bottom: 0; width: 100%; display: flex; justify-content: space-between; padding-top: 10px; padding-bottom: 10px; background-color: var(--background-dk); z-index: 100; }
.k-form-buttons.first-section { justify-content: flex-end; }
.k-form-buttons .k-button { width: 49%; font-size: 1.8rem; cursor: pointer; }
.k-form-buttons .back-btn { background-color: var(--background); border: 1px solid var(--primary); color: var(--primary); }
.k-form-buttons .next-btn { background-color: var(--primary); color: var(--background); }
.k-form-buttons .next-btn:disabled { opacity: 0.65; cursor: not-allowed; }

/* Side menu */
.side-menu-container { width: 28%; background-color: var(--background-dk); border-radius: 12px; overflow: hidden; padding: 45px 15px; display: flex; flex-direction: column; height: 100%; position: relative; }
.side-panels { flex-grow: 1; overflow: auto; }
.side-panels::-webkit-scrollbar { display: none; }
.panel-container { padding: 15px 0; border-bottom: 1px solid var(--secondary-ltr); cursor: pointer; }
.panel-container:hover .k-expander-title { color: var(--on-background); }
.k-expander.menu-panel { background-color: var(--background-dk); border-radius: 8px; border: 0; }
.k-expander.menu-panel.active-section .k-expander-title { color: var(--on-background); font-weight: 600; }
.k-expander-header { display: flex; align-items: center; padding: 6px 4px; }
.k-expander-title { text-transform: none; color: var(--text-secondary-on-background); font-weight: normal; display: flex; width: 100%; justify-content: space-between; align-items: center; font-size: 1.8rem; }
.k-spacer { flex: 1; }
.menu-icon { font-size: 1.6rem; }
.menu-icon.completed { color: var(--con-primary-lt); }

/* ── No co-borrower message ── */
.rm-no-coborrower-msg {
  font-size: 1.6rem;
  color: var(--secondary-dkr);
  font-style: italic;
  margin-top: 16px;
  padding: 14px 18px;
  background: var(--background);
  border: 1px solid var(--secondary-ltr);
  border-radius: 8px;
  line-height: 1.5;
}
.rm-no-coborrower-msg i { margin-right: 6px; }

/* ── Success screen ── */
.rm-success-screen { width: 70%; display: flex; align-items: center; justify-content: center; background-color: var(--background-dk); border-radius: 12px; height: 100%; padding: 45px 35px; }
.rm-success-inner { display: flex; flex-direction: column; align-items: center; text-align: center; max-width: 520px; width: 100%; }
.rm-success-icon { font-size: 7rem; margin-bottom: 24px; --fa-primary-color: #198754; --fa-secondary-color: #c3e6cb; --fa-secondary-opacity: 0.8; color: #198754; }
.rm-success-title { font-size: 2.8rem; font-weight: 700; color: #198754; margin: 0 0 14px; font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; }
.rm-success-msg { font-size: 1.8rem; color: var(--text-secondary-on-background); line-height: 1.6; margin: 0 0 28px; font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; }
.rm-success-details { width: 100%; background: var(--background); border: 1px solid var(--secondary-ltr); border-radius: 8px; padding: 16px 20px; margin-bottom: 28px; text-align: left; }
.rm-success-detail-row { display: flex; align-items: baseline; gap: 12px; padding: 6px 0; border-bottom: 1px solid var(--secondary-ltr); }
.rm-success-detail-row:last-child { border-bottom: none; }
.rm-success-detail-label { font-size: 1.6rem; font-weight: 600; color: var(--text-secondary-on-background); min-width: 100px; font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; }
.rm-success-detail-value { font-size: 1.7rem; color: var(--on-background); font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; }
.rm-success-close-btn { background-color: var(--primary) !important; color: var(--background) !important; font-size: 1.8rem !important; min-width: 160px; height: 44px; }

.success-fade-enter-active, .success-fade-leave-active { transition: opacity 0.3s ease; }
.success-fade-enter-from, .success-fade-leave-to { opacity: 0; }

/* Toasts */
.rm-toast { position: fixed; bottom: 24px; right: 24px; z-index: 9999; padding: 14px 22px; border-radius: 8px; font-size: 1.6rem; font-weight: 600; display: flex; align-items: center; gap: 10px; box-shadow: 0 8px 24px rgba(5, 29, 58, 0.14); }
.rm-toast-error { background: var(--error-ltr); border: 1px solid var(--error); color: var(--error); }
.toast-slide-enter-active, .toast-slide-leave-active { transition: opacity .25s ease, transform .25s ease; }
.toast-slide-enter-from, .toast-slide-leave-to { opacity: 0; transform: translateY(10px); }

/* Responsive */
@media only screen and (max-width: 1200px) {
  .home-view.rm-home-view { padding: 25px 10px 10px 10px; overflow: auto; }
  .loan-section-container { font-size: 2rem; }
  .dba-logo-container .dba-logo { height: 65px; }
  .lo-img-container, .lo-img-container .lo-img { height: 65px; width: 65px; }
  .app-header { padding: 10px 20px; }
}

@media only screen and (max-width: 800px) {
  .rm-application-form { width: 100%; padding: 25px 20px 20px 20px; }
  .side-menu-container { display: none; }
  .form-row { flex-wrap: wrap; }
  .form-row-item.pair, .form-row-item.trio { width: 100%; margin-left: 0; margin-right: 0; }
  .application-form-content { padding-right: 0; }
  .dba-logo-container .dba-logo { height: 50px; }
  .lo-img-container { display: none; }
  .app-header { padding: 8px 14px; }
}
</style>